import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import type { AnswerValue, Entry, MedicationSetting, Scenario, Subscription } from "@/domain/entities";
import { ScenarioRunner, type ValidationCode } from "@/domain/scenario/ScenarioRunner";
import { AnswerValidationError } from "@/services/EntryService";
import { Button } from "../components/Button";
import { QuestionInput } from "../components/questions/QuestionInput";
import { Screen } from "../components/Screen";
import { useApp } from "../container";
import { formatAnswer, formatDate } from "../format";
import { useQuery } from "../hooks/useQuery";
import { format } from "../i18n/strings";
import { useLocale } from "../i18n/LocaleProvider";
import styles from "./QuestionnaireScreen.module.css";

interface Loaded {
  subscription: Subscription;
  scenario: Scenario;
  entry: Entry;
  /** Settings medicines first, then names remembered from earlier entries of this subscription. */
  medicines: MedicationSetting[];
}

export function QuestionnaireScreen() {
  const { subscriptionId = "", date = "", entryId } = useParams();
  const app = useApp();
  const navigate = useNavigate();
  const loaded = useQuery<Loaded | null>(async () => {
    const subscription = await app.store.subscriptions.get(subscriptionId);
    if (!subscription) return null;
    const scenario = await app.store.scenarios.get(subscription.scenarioId);
    if (!scenario) return null;
    const entry = entryId ? await app.store.entries.get(entryId) : await app.entries.getOrCreate(subscription, scenario, date);
    if (!entry) return null;
    const configured = subscription.settings.medications ?? [];
    const remembered = (await app.entries.medicineHistory(subscription.id, scenario)).filter(
      (m) => !configured.some((c) => c.name.toLowerCase() === m.name.toLowerCase()),
    );
    return { subscription, scenario, entry, medicines: [...configured, ...remembered] };
  }, [subscriptionId, date, entryId ?? ""]);

  if (loaded.data === undefined) return null;
  if (loaded.data === null) return <NotFound />;
  const { entry, scenario } = loaded.data;
  return (
    <Questionnaire
      key={entry.id}
      {...loaded.data}
      onDone={() => {
        app.analytics.track("entry_saved", { scenarioId: scenario.slug });
        void app.reminders.markDone(loaded.data!.subscription.id);
        navigate("/");
      }}
      onCancel={() => app.entries.cancelEdits(entry).then(() => navigate("/"))}
    />
  );
}

function NotFound() {
  const { t } = useLocale();
  const navigate = useNavigate();
  return <Screen title={t.today.title} back={() => navigate(-1)}>{t.day.noEntry}</Screen>;
}

interface Actions {
  onDone: () => void;
  onCancel: () => void;
}

function Questionnaire({ scenario, entry: initial, medicines, onDone, onCancel }: Loaded & Actions) {
  const app = useApp();
  const navigate = useNavigate();
  const { t, pick, locale } = useLocale();
  const runner = useMemo(() => new ScenarioRunner(scenario), [scenario]);
  // A finished entry opens on its first question, ready to change; Back from there shows the summary.
  const finished = runner.next(initial.answers) === null;
  const [entry, setEntry] = useState(initial);
  const [currentId, setCurrentId] = useState<string | null>(finished ? (runner.visibleQuestions(initial.answers)[0]?.id ?? null) : null);
  const [error, setError] = useState<ValidationCode | null>(null);
  const [reviewing, setReviewing] = useState(false);
  /** Set while a single answer is being changed from the summary: Back returns there. */
  const [backToSummary, setBackToSummary] = useState(finished);

  const visible = runner.visibleQuestions(entry.answers);
  const current = (currentId && visible.find((q) => q.id === currentId)) || runner.next(entry.answers);

  const submit = async (value: AnswerValue | undefined) => {
    if (!current) return;
    try {
      const next = await app.entries.answer(entry, scenario, current.id, value);
      setEntry(next);
      setError(null);
      const following = runner.next(next.answers);
      setCurrentId(following?.id ?? null);
      setBackToSummary(false);
      if (!following) setReviewing(true);
    } catch (cause) {
      if (cause instanceof AnswerValidationError) setError(cause.code);
      else throw cause;
    }
  };

  const back = () => {
    if (!current) return;
    if (backToSummary) {
      setBackToSummary(false);
      setReviewing(true);
      return;
    }
    const index = visible.findIndex((q) => q.id === current.id);
    if (index > 0) setCurrentId(visible[index - 1]?.id ?? null);
    else navigate(-1);
  };

  if (reviewing || !current) {
    return (
      <Summary
        scenario={scenario}
        entry={entry}
        onEdit={(questionId) => {
          setCurrentId(questionId);
          setReviewing(false);
          setBackToSummary(true);
        }}
        onDone={onDone}
        onCancel={onCancel}
      />
    );
  }

  const index = visible.findIndex((q) => q.id === current.id) + 1;
  return (
    <Screen title={pick(scenario.name)} back={back}>
      <p className={styles.progress}>
        {formatDate(entry.date, locale)} · {format(t.questionnaire.progress, { index, total: visible.length })}
      </p>
      <div key={current.id} className="step-in">
        <h2 className={styles.question}>{pick(current.text)}</h2>
        {current.hint && <p className={styles.hint}>{pick(current.hint)}</p>}
        <div style={{ height: 16 }} />
        <QuestionInput
          question={current}
          value={entry.answers[current.id]}
          medicines={medicines}
          error={error}
          onSubmit={submit}
        />
      </div>
      <div className={styles.cancelRow}>
        <Button variant="ghost" block onClick={onCancel}>{t.common.cancel}</Button>
      </div>
    </Screen>
  );
}

function Summary({ scenario, entry, onEdit, onDone, onCancel }: Omit<Loaded, "medicines" | "subscription"> & Actions & { onEdit: (questionId: string) => void }) {
  const { t, pick, locale } = useLocale();
  const runner = useMemo(() => new ScenarioRunner(scenario), [scenario]);

  return (
    <Screen title={t.questionnaire.summaryTitle}>
      <p className={styles.progress}>{formatDate(entry.date, locale)}</p>
      <div className={styles.summary}>
        {runner.visibleQuestions(entry.answers).map((question) => (
          <button key={question.id} type="button" className={styles.summaryRow} onClick={() => onEdit(question.id)}>
            <span className={styles.summaryQuestion}>{pick(question.text)}</span>
            <span>{formatAnswer(question, entry.answers[question.id], locale, t)}</span>
          </button>
        ))}
      </div>
      <div className={styles.actions}>
        <Button variant="ghost" onClick={onCancel}>{t.common.cancel}</Button>
        <Button variant="primary" onClick={onDone}>{t.common.save}</Button>
      </div>
    </Screen>
  );
}
